import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Column } from 'primereact/column';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { addDays, endOfDay, format, parseISO, startOfDay, subDays } from 'date-fns';
import { Range as IDateRange } from 'react-date-range';
import BaseDatatable from '@abstract/abstractwebcommon-client/Table/BaseDatatable';
import DatatableColumn from '@abstract/abstractwebcommon-client/Table/DatatableColumn';
import SearchBar from '@abstract/abstractwebcommon-client/SearchBar/SearchBar';
import { defaultTableLimit } from '@abstract/abstractwebcommon-client/Constants';
import { ManualDateRangePicker } from '@abstract/abstractwebcommon-client/DateRangePicker/dateRangePicker';
import {
  IPageEvent,
  ISortEvent,
  ITablePayload,
} from '@abstract/abstractwebcommon-shared/interfaces/pagination';
import { formatDate } from '@abstract/abstractwebcommon-shared/utils/sharedFunctions';
import { IFileRecordStateSelector, IStateSelectors } from '../../../Interfaces/Selectors';
import { getDownloadTracking } from '../../../Store/FileRecords';
import {
  IFileDownload,
  IFileDownloadDailyCount,
  readDailyDownloadsAPI,
} from '../../../Services/FileRecord';
import { translate } from '../../../Utils/Translate';
import { asyncErrorHandler } from '@abstract/abstractwebcommon-shared/utils/AsyncErrorHandler';
import { handleError } from '@abstract/abstractwebcommon-client/ErrorHandler/ErrorHandler';
import './Style.css';

interface IDownloadTrackingPayload extends ITablePayload {
  fileName?: string;
  startDate?: string;
  endDate?: string;
}

const DownloadTrackingPage = (): JSX.Element => {
  const dispatch: Dispatch<any> = useDispatch();
  const fileRecordState: IFileRecordStateSelector = useSelector(
    (state: IStateSelectors) => state.fileRecords
  );
  const defaultDate: IDateRange[] = [
    {
      startDate: startOfDay(subDays(new Date(), 29)),
      endDate: endOfDay(new Date()),
      key: 'selection',
    },
  ];
  const node: any = useRef();
  const [selectedDateRange, setSelectedDateRange] = useState<IDateRange[]>(defaultDate);
  const [dailyDownloads, setDailyDownloads] = useState<IFileDownloadDailyCount[]>([]);
  const [isDailyDownloadsLoading, setDailyDownloadsLoading] = useState<boolean>(false);
  const [payload, setPayload] = useState<IDownloadTrackingPayload>({
    limit: defaultTableLimit,
    skip: 0,
    sort: {
      sortField: 'downloadedAt',
      sortOrder: -1,
    },
    searchTerm: '',
    fileName: '',
    startDate: defaultDate[0].startDate.toISOString(),
    endDate: defaultDate[0].endDate.toISOString(),
  });

  const fetchDailyDownloads = async (nextPayload: IDownloadTrackingPayload): Promise<void> => {
    setDailyDownloadsLoading(true);
    const result = await asyncErrorHandler(
      readDailyDownloadsAPI(
        nextPayload.searchTerm || '',
        nextPayload.fileName || '',
        nextPayload.startDate || defaultDate[0].startDate.toISOString(),
        nextPayload.endDate || defaultDate[0].endDate.toISOString(),
        new Date().getTimezoneOffset()
      )
    );
    if (result?.error) {
      handleError({ message: result.error.message });
    } else {
      setDailyDownloads(result?.data || []);
    }
    setDailyDownloadsLoading(false);
  };

  const fetchDownloads = (nextPayload: IDownloadTrackingPayload): void => {
    dispatch(getDownloadTracking(nextPayload));
    fetchDailyDownloads(nextPayload);
  };

  const handleSortUpdate = (event: ISortEvent): void => {
    const updatedPayload: IDownloadTrackingPayload = {
      ...payload,
      sort: event,
    };
    setPayload(updatedPayload);
    fetchDownloads(updatedPayload);
  };

  const handlePageUpdate = (event: IPageEvent): void => {
    const updatedPayload: IDownloadTrackingPayload = {
      ...payload,
      skip: event.first,
      limit: event.rows,
    };
    setPayload(updatedPayload);
    fetchDownloads(updatedPayload);
  };

  const handleFilterUpdate = (searchTerm: string): void => {
    const updatedPayload: IDownloadTrackingPayload = {
      ...payload,
      skip: 0,
      searchTerm,
    };
    setPayload(updatedPayload);
    fetchDownloads(updatedPayload);
  };

  const handleFileNameFilterUpdate = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedPayload: IDownloadTrackingPayload = {
      ...payload,
      skip: 0,
      fileName: event.target.value,
    };
    setPayload(updatedPayload);
    fetchDownloads(updatedPayload);
  };

  const handleDateRangeChange = (event: any): void => {
    const nextDateRange: IDateRange[] = [event.selection];
    setSelectedDateRange(nextDateRange);
    const updatedPayload: IDownloadTrackingPayload = {
      ...payload,
      skip: 0,
      startDate: startOfDay(new Date(nextDateRange[0].startDate)).toISOString(),
      endDate: endOfDay(new Date(nextDateRange[0].endDate)).toISOString(),
    };
    setPayload(updatedPayload);
    fetchDownloads(updatedPayload);
  };

  const clearFilters = (): void => {
    const updatedPayload: IDownloadTrackingPayload = {
      ...payload,
      skip: 0,
      searchTerm: '',
      fileName: '',
      startDate: defaultDate[0].startDate.toISOString(),
      endDate: defaultDate[0].endDate.toISOString(),
    };
    setSelectedDateRange(defaultDate);
    setPayload(updatedPayload);
    fetchDownloads(updatedPayload);
  };

  useEffect(() => {
    fetchDownloads(payload);
  }, []);

  const filters: JSX.Element = (
    <div className="d-flex justify-content-between align-items-center mobile-screen-width-search-bar mb-3">
      <div className="d-flex headerTableContainer">
        <ManualDateRangePicker
          node={node}
          date={selectedDateRange}
          defaultDate={defaultDate}
          handleDateChange={handleDateRangeChange}
        />
        <Button className="ml-1" variant="secondary" onClick={clearFilters}>
          {translate('admin.page.downloadTracking.filters.clear')}
        </Button>
      </div>
      <div className="d-flex headerTableContainer header-search-filter">
        <Form.Control
          className="mr-1"
          placeholder={translate('admin.page.downloadTracking.filters.fileName')}
          value={payload.fileName}
          onChange={handleFileNameFilterUpdate}
        />
        <SearchBar onSearchTermChanged={(data: string) => handleFilterUpdate(data)} />
      </div>
    </div>
  );

  const getChartData = (): IFileDownloadDailyCount[] => {
    const startDate = startOfDay(new Date(selectedDateRange[0].startDate));
    const endDate = startOfDay(new Date(selectedDateRange[0].endDate));
    const downloadsByDate: Record<string, number> = {};
    dailyDownloads.forEach((item: IFileDownloadDailyCount) => {
      downloadsByDate[item.date] = item.count;
    });

    const days: IFileDownloadDailyCount[] = [];
    let currentDate = startDate;
    while (currentDate <= endDate) {
      const key = format(currentDate, 'yyyy-MM-dd');
      days.push({
        date: key,
        count: downloadsByDate[key] || 0,
      });
      currentDate = addDays(currentDate, 1);
    }
    return days;
  };

  const chartData: IFileDownloadDailyCount[] = getChartData();
  const maxDownloads: number = Math.max(...chartData.map((item) => item.count), 1);
  const totalDownloads: number = chartData.reduce((total, item) => total + item.count, 0);

  const downloadChart: JSX.Element = (
    <div className="download-tracking-chart mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h5 className="mb-0">{translate('admin.page.downloadTracking.chart.title')}</h5>
          <small>
            {translate('admin.page.downloadTracking.chart.total', {
              total: `${totalDownloads}`,
            })}
          </small>
        </div>
        {isDailyDownloadsLoading && (
          <small>{translate('admin.page.downloadTracking.chart.loading')}</small>
        )}
      </div>
      <div className="download-tracking-chart-bars">
        {chartData.map((item: IFileDownloadDailyCount) => (
          <div className="download-tracking-chart-day" key={item.date}>
            <div
              className="download-tracking-chart-bar"
              style={{ height: `${Math.max((item.count / maxDownloads) * 100, item.count ? 8 : 2)}%` }}
            >
              <div className="download-tracking-chart-tooltip">
                <strong>{item.count}</strong>
                <span>{translate('admin.page.downloadTracking.chart.downloads')}</span>
                <small>{format(parseISO(item.date), 'MMM d')}</small>
              </div>
            </div>
            <span>{format(parseISO(item.date), 'd')}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {filters}
      {downloadChart}
      <Row>
        <Col>
          <BaseDatatable
          value={fileRecordState.downloadTrackingList?.records}
          totalRecords={fileRecordState.downloadTrackingList?.totalRecords}
          isLoading={fileRecordState.downloadTrackingIsFetching}
          header={<></>}
          parentClass="downloadTrackingDataTable"
          first={payload.skip}
          emptyMessage={translate('admin.page.downloadTracking.table.empty_msg')}
          rows={payload.limit}
          onPage={handlePageUpdate}
          sortField={payload.sort.sortField}
          onSort={handleSortUpdate}
          sortOrder={payload.sort.sortOrder}
          bodyStyle={'text-center'}
          responsive>
          <Column
            sortable
            field="fileName"
            body={(rowData: IFileDownload) => (
              <DatatableColumn
                title={translate('admin.page.downloadTracking.table.fileName')}
                data={rowData.fileName}
              />
            )}
            header={translate('admin.page.downloadTracking.table.fileName')}
          />
          <Column
            sortable
            field="versionName"
            body={(rowData: IFileDownload) => (
              <DatatableColumn
                title={translate('admin.page.downloadTracking.table.versionName')}
                data={rowData.versionName || rowData.versionID}
              />
            )}
            header={translate('admin.page.downloadTracking.table.versionName')}
          />
          <Column
            sortable
            field="downloadedByEmail"
            body={(rowData: IFileDownload) => (
              <DatatableColumn
                title={translate('admin.page.downloadTracking.table.downloadedByEmail')}
                data={rowData.downloadedByEmail || rowData.downloadedByUUID}
              />
            )}
            header={translate('admin.page.downloadTracking.table.downloadedByEmail')}
          />
          <Column
            sortable
            field="downloadedAt"
            body={(rowData: IFileDownload) => (
              <DatatableColumn
                title={translate('admin.page.downloadTracking.table.downloadedAt')}
                data={formatDate(rowData.downloadedAt, { isTime: true })}
              />
            )}
            header={translate('admin.page.downloadTracking.table.downloadedAt')}
            className="createdDateCol"
          />
          </BaseDatatable>
        </Col>
      </Row>
    </>
  );
};

export default DownloadTrackingPage;
